import React, {Component} from 'react'
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import ExclamationTriangleIcon from "@heroicons/react/24/outline/ExclamationTriangleIcon";
import Resources from "../../../data/services/resources";
import {classNames} from "../../util/util-helpers";
import {
    MILEAGE_CALCULATION_METHOD_ADDRESS_TO_ADDRESS_PRACTICAL,
    MILEAGE_CALCULATION_METHOD_GEO_LOCATION_PRACTICAL, MILEAGE_CALCULATION_METHOD_ZIP_TO_ZIP_PRACTICAL
} from "../../util/util-consts";

TrimbleMaps.APIKey = import.meta.env.VITE_TRIMBLE_API_KEY;

function checkWebGLSupport() {
    try {
        const canvas = document.createElement('canvas');
        return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch (e) {
        return false;
    }
}

const isWebGLAvailable = checkWebGLSupport();

/**
 * Calculate bearing (in degrees) from point A to point B.
 * Points are {Longitude, Latitude} in degrees.
 * Returns 0-360 where 0 = North, 90 = East, etc.
 */
function calculateBearing(from, to) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const toDeg = (rad) => (rad * 180) / Math.PI;

    const lat1 = toRad(from.Latitude);
    const lat2 = toRad(to.Latitude);
    const dLng = toRad(to.Longitude - from.Longitude);

    const y = Math.sin(dLng) * Math.cos(lat2);
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

    return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

export default class TrimbleMap extends Component {
    constructor(props) {
        super(props);
        this.myMap = this.props.mapRef;
        this.isRouteApproximationEnabled = !!this.props.isRouteApproximationEnabled;
    }

    componentDidMount() {
        if (!this.myMap.current) return;

        if (!isWebGLAvailable) return;

        try {
            this.setupMap();
        }
        catch (error) {
            console.error("WebGL initialization failed:", error);
        }
    }

    componentWillUnmount() {
        if (this.myMap.current) {
            this.myMap.current.remove(); // Proper cleanup
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.resource?.data?.list !== this.props.resource?.data?.list && this.props.resource?.resource === Resources.LoadTracks) {
            this.setupMap();
        }
    }

    setupMap = () => {
        const routeStops = this.props.stops
            // Stops have MultistopLongitude
            // StopOff have Longitude
            .filter(stop => (!!stop?.MultistopLongitude || !!stop?.Longitude) && (!!stop?.MultistopLatitude || !!stop?.Latitude))
            .map(stop => {
                return new TrimbleMaps.LngLat(stop?.MultistopLongitude ?? stop?.Longitude, stop?.MultistopLatitude ?? stop?.Latitude);
            });

        this.myMap.current = new TrimbleMaps.Map({
            container: this.props.mapContainerID ?? "trimbleMap",
            center: new TrimbleMaps.LngLat(-96, 35),
            zoom: 3,
            style: TrimbleMaps.Common.Style.TRANSPORTATION,
            region: TrimbleMaps.Common.Region.NA,
            displayControlsDefault: true
        });

        this.myMap.current.addControl(new TrimbleMaps.NavigationControl());

        let routeOptions = {};

        if (this.isRouteApproximationEnabled && !!routeStops.length) {
            routeOptions = {
                routeId: "route",
                stops: routeStops
            };
        }

        const isPractical = this.props?.mapOptions?.CalculateMileageUsingZipOnly
            ? [
                MILEAGE_CALCULATION_METHOD_ADDRESS_TO_ADDRESS_PRACTICAL,
                MILEAGE_CALCULATION_METHOD_GEO_LOCATION_PRACTICAL,
                MILEAGE_CALCULATION_METHOD_ZIP_TO_ZIP_PRACTICAL
            ].includes(this.props.mapOptions.CalculateMileageUsingZipOnly)
            : true;
        routeOptions.tollDiscourage = this.props?.mapOptions?.TollDiscourage ?? false;
        routeOptions.bordersOpen = this.props?.mapOptions?.BordersOpen ?? true;
        routeOptions.routeType = isPractical ? TrimbleMaps.Common.RouteType.PRACTICAL : TrimbleMaps.Common.RouteType.SHORTEST;
        routeOptions.vehicleType = TrimbleMaps.Common.VehicleType.TRUCK;

        const myRoute = new TrimbleMaps.Route(routeOptions);

        this.myMap.current.on('load', () => {
            this.props.stops.forEach((stop, index) => {
                const stopLngLat = new TrimbleMaps.LngLat(stop.Longitude, stop.Latitude)
                const {LocationName, AddressName, CityName, StateAbbreviation, IsStopCompleted} = stop
                const title = LocationName + (AddressName ? '\n' + AddressName : "") + '\n' + CityName + ', ' + StateAbbreviation;


                let stopColor = ''

                if (IsStopCompleted) {
                    stopColor = '#32CD32'
                } else {
                    stopColor = '#6495ED'
                }

                let content;
                content =
                    ` <svg class="w-8 h-8">
                            <circle cx="16" cy="16" r="16" fill=${stopColor} />
                            <text x="50%" y="50%" text-anchor="middle" fill="white" font-size="20px" font-family="Arial" dy=".3em">${index + 1}</text>
                            </svg>`


                const svgContent = document.createElement('div');
                svgContent.title = title
                svgContent.innerHTML = content;
                new TrimbleMaps.Marker({
                    element: svgContent
                }).setLngLat([stopLngLat.lng, stopLngLat.lat]).addTo(this.myMap.current);
            })

            if (Object.keys(routeOptions).length) {
                myRoute.addTo(this.myMap.current);
            }

            // Add tracks: truck icon at first point (most recent), arrow markers at remaining points
            if (this.props?.tracks?.length > 0) {
                const tracks = this.props.tracks;

                let truckLocation = [];
                tracks.forEach((it, index) => {
                    if (!it?.Longitude || !it?.Latitude) return;

                    const trackLngLat = new TrimbleMaps.LngLat(it.Longitude, it.Latitude);
                    const {AddressName, CityName} = it;
                    const title = (AddressName ? '\n' + AddressName : "") + '\n' + CityName;

                    if (index === 0) {
                        truckLocation = [trackLngLat.lng, trackLngLat.lat]
                    } else {
                        // All other track points — small directional arrow marker
                        // Calculate bearing: from this point toward the previous one (closer to index 0 = more recent)
                        const nextToward = tracks[index - 1];
                        if (!nextToward?.Longitude || !nextToward?.Latitude) return;

                        const bearing = calculateBearing(it, nextToward);

                        const arrowEl = document.createElement('div');
                        arrowEl.title = title;
                        arrowEl.innerHTML =
                            `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
                                <path d="M12 2 L20 18 L12 14 L4 18 Z" fill="#333333" stroke="#ffffff" stroke-width="1.5"/>
                            </svg>`;

                        new TrimbleMaps.Marker({
                            element: arrowEl,
                            anchor: 'center',
                            rotation: bearing,
                            rotationAlignment: 'map'
                        }).setLngLat([trackLngLat.lng, trackLngLat.lat]).addTo(this.myMap.current);
                    }
                });

                if (truckLocation.length > 0) {
                    // First/current location — truck icon (unchanged)
                    const content = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#42a318" stroke-width="1" stroke="currentColor" class="5 h-5">' +
                        '<path d="M3.375 4.5C2.339 4.5 1.5 5.34 1.5 6.375V13.5h12V6.375c0-1.036-.84-1.875-1.875-1.875h-8.25zM13.5 15h-12v2.625c0 1.035.84 1.875 1.875 1.875h.375a3 3 0 116 0h3a.75.75 0 00.75-.75V15z"/>' +
                        '<path d="M8.25 19.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0zM15.75 6.75a.75.75 0 00-.75.75v11.25c0 .087.015.17.042.248a3 3 0 015.958.464c.853-.175 1.522-.935 1.464-1.883a18.659 18.659 0 00-3.732-10.104 1.837 1.837 0 00-1.47-.725H15.75z"/>' +
                        '<path d="M19.5 19.5a1.5 1.5 0 10-3 0 1.5 1.5 0 003 0z"/>' +
                        '</svg>';

                    const svgContent = document.createElement('div');
                    svgContent.innerHTML = content;
                    new TrimbleMaps.Marker({
                        element: svgContent
                    }).setLngLat(truckLocation).addTo(this.myMap.current);
                }
            }

            // Optional: thin connecting line behind the arrows for continuity
            if (this.props?.tracks?.length > 1) {
                const tracksCoordinates = this.props.tracks.reduce((memo, it) => {
                    if (it?.Longitude && it?.Latitude) {
                        memo.push([it.Longitude, it.Latitude]);
                    }
                    return memo;
                }, []);

                let geoJsonActualPathData = {
                    type: 'FeatureCollection',
                    features: [{
                        type: 'Feature',
                        properties: {
                            name: 'Actual Path'
                        },
                        geometry: {
                            type: 'LineString',
                            coordinates: tracksCoordinates
                        }
                    }]
                };

                this.myMap.current.addSource('actualPath', {
                    type: 'geojson',
                    data: geoJsonActualPathData
                });

                this.myMap.current.addLayer({
                    id: 'actualPathLayer',
                    type: 'line',
                    source: 'actualPath',
                    paint: {
                        'line-width': 2,
                        'line-color': '#333333',
                        'line-opacity': 0.3
                    }
                });
            }

            //this.myMap.on('click', (e) => console.log(e)); // on map click example
        });
    }

    render() {
        if (!isWebGLAvailable) {
            return (
                <div className={classNames(
                    "flex flex-col items-center justify-center bg-tm-gray-50 text-tm-gray-700 text-center px-5",
                    this.props.className ?? "border border-tm-gray-300 rounded-lg overflow-hidden mt-6"
                )}>
                    <ExclamationTriangleIcon className="w-10 h-10 text-yellow-600 mb-2"/>
                    <p className="text-lg font-semibold">
                        {this.props.translate("text.map_unavailable")}
                    </p>
                    <p className="text-sm text-tm-gray-500 mt-1">
                        {this.props.translate("text.webgl_not_available")}
                    </p>
                </div>
            )
        }

        return (
            <div
                ref={this.myMap}
                id={this.props.mapContainerID ?? "trimbleMap"}
                className={this.props.className ?? "border border-tm-gray-300 rounded-lg overflow-hidden mt-6"}
            />
        )
    }
}